import React, { useState, useEffect, useMemo } from "react";
import Layout from "components/sales/layout/layout";
import localization from "assets/localization/localization";
import { ReportingHierarchyChart } from "components/UI";
import { fetchReportingHierarchy } from "services/api/userApi";
import './profile.css';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

const Profile = ({ userProfile }) => {
  const [isHierarchyLoading, setIsHierarchyLoading] = useState(true);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [hierarchyError, setHierarchyError] = useState(null);

  const designation = userProfile?.roles?.[0]?.title;
  const department = userProfile?.teams?.[0]?.department_name;
  const teams = useMemo(() => {
    if (!userProfile?.teams?.length) return '';
    const names = [...new Set(userProfile.teams.map((t) => t.team_name).filter(Boolean))];
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
  }, [userProfile]);

  const headerSubtitle = useMemo(() => {
    const parts = [designation, department || teams].filter(Boolean);
    return parts.join(' | ');
  }, [designation, department, teams]);

  useEffect(() => {
    let isMounted = true;

    const loadHierarchy = async () => {
      setIsHierarchyLoading(true);
      setHierarchyError(null);
      try {
        const data = await fetchReportingHierarchy();
        if (isMounted) {
          setHierarchyData(data);
        }
      } catch (err) {
        if (isMounted) {
          setHierarchyError(err?.message || 'Failed to load organization hierarchy');
        }
      } finally {
        if (isMounted) {
          setIsHierarchyLoading(false);
        }
      }
    };

    loadHierarchy();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Layout title={localization.myProfile} className="profile-layout">
      <div className="profile-page">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-header__avatar">
              {getInitials(userProfile?.name)}
            </div>
            <div className="profile-header__info">
              <h2 className="profile-header__name">
                {userProfile?.name || localization.na}
              </h2>
              {headerSubtitle && (
                <div className="profile-header__subtitle">{headerSubtitle}</div>
              )}
              {userProfile?.email && (
                <div className="profile-header__email">{userProfile.email}</div>
              )}
            </div>
          </div>

          <div className="profile-tree-section">
            {hierarchyError ? (
              <p className="profile-hierarchy-error">{hierarchyError}</p>
            ) : (
              <ReportingHierarchyChart
                data={hierarchyData}
                isLoading={isHierarchyLoading}
                currentUserDesignation={designation || ''}
                currentUserDepartment={department || ''}
                youLabel={localization.you}
                showFullChainLabel={localization.showFullChain}
                showLessLabel={localization.showLess}
                noHierarchyLabel={localization.noHierarchyFound}
                directLabel={localization.directLabel}
                totalReporteesLabel={localization.totalReporteesLabel}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
